// A group's description is sealed with the chat's key like a text message
// (M63, D234): the server stores it and can't read it, and devices open it
// with the same code as messages (openAll).
import { api, ApiError } from '../api';
import { isE2EEReady, nextGroupRevision, sendSealed } from './session';

export class KeysNotReadyError extends Error {
  constructor() {
    super('KEYS_NOT_READY');
  }
}

/**
 * Saves a group's name and/or description. A description (trimmed) is
 * sealed on this device first; an empty one clears it. Seals again once if
 * the chat moved to a newer key meanwhile.
 */
export async function saveGroupInfo(
  conversationId: string,
  change: { name?: string; description?: string }
) {
  const name = change.name !== undefined ? { name: change.name } : {};
  if (change.description === undefined) {
    return api.updateConversation(conversationId, name);
  }
  const text = change.description.trim();
  if (!text) {
    return api.updateConversation(conversationId, {
      ...name,
      description: null,
    });
  }
  if (!isE2EEReady()) throw new KeysNotReadyError();
  // Bound to its revision, enforced like an edit (D326): once more after
  // the server's if someone changed it meanwhile.
  const attempt = (atLeast: number) => {
    const clientId = crypto.randomUUID();
    const revision = nextGroupRevision(conversationId, 'description', atLeast);
    return sendSealed(
      conversationId,
      clientId,
      { v: 1, text },
      { group: 'description', revision },
      (sealed) =>
        api.updateConversation(conversationId, {
          ...name,
          description: { ...sealed, clientId, revision },
        })
    );
  };
  try {
    return await attempt(0);
  } catch (err) {
    const stored =
      err instanceof ApiError
        ? (err.details?.description ?? err.details?.revision)
        : null;
    if (
      err instanceof ApiError &&
      err.code === 'STALE_REVISION' &&
      typeof stored === 'number'
    ) {
      return attempt(stored);
    }
    throw err;
  }
}
